// IndexedDB Database Initialization with Dexie.js
// Provides offline storage with security and sync capabilities

import Dexie from 'dexie';
import { schema, SYNC_STATUS, SYNC_ACTIONS, ENTITY_TYPES } from './schema';
import { hashData, verifyDataHash, signData, verifySignature, generateSecureId } from '../utils/dataIntegrity';

class TrackProfitDB extends Dexie {
  constructor() {
    super('TrackProfitDB');
    
    // Define database version and stores
    this.version(1).stores({
      // Orders table
      orders: 'orderId, orgId, accountId, syncStatus, localModifiedAt, serverModifiedAt, [orgId+accountId], [orgId+syncStatus], [accountId+syncStatus]',
      
      // Expenses table  
      expenses: 'expenseId, orgId, accountId, syncStatus, localModifiedAt, serverModifiedAt, [orgId+accountId], [orgId+syncStatus]',
      
      // Customers table
      customers: 'customerId, orgId, syncStatus, name, phone, [orgId+name], [orgId+phone]',
      
      // Products table
      products: 'productId, orgId, syncStatus, name, [orgId+name], [orgId+syncStatus]',
      
      // Money accounts table
      moneyAccounts: 'accountId, orgId, syncStatus, [orgId+syncStatus]',
      
      // Categories table
      categories: 'categoryId, orgId, type, syncStatus, [orgId+type], [orgId+syncStatus]',
      
      // Organization accounts table
      orgAccounts: 'accountId, orgId, syncStatus, [orgId+syncStatus]',
      
      // Sync queue table
      syncQueue: 'queueId, entity, entityId, createdAt, retryCount',
      
      // User permissions table
      userPermissions: 'userId, accountId, cachedAt',
      
      // Metadata table
      metadata: 'key'
    });

    // Add hooks for data integrity
    this.setupHooks();
  }

  setupHooks() {
    // Hook for orders table
    this.orders.hook('creating', (primKey, obj, trans) => {
      obj.orderId = obj.orderId || generateSecureId();
      obj.dataHash = hashData(obj);
      obj.syncStatus = obj.syncStatus || SYNC_STATUS.PENDING_CREATE;
      obj.localModifiedAt = Date.now();
      obj.createdDate = Date.now();
      obj.updatedDate = Date.now();
    });

    this.orders.hook('updating', (modifications, primKey, obj, trans) => {
      modifications.dataHash = hashData({ ...obj, ...modifications });
      modifications.localModifiedAt = Date.now();
      modifications.updatedDate = Date.now();
      modifications.syncStatus = modifications.syncStatus || SYNC_STATUS.PENDING_UPDATE;
    });

    // Hook for expenses table
    this.expenses.hook('creating', (primKey, obj, trans) => {
      obj.expenseId = obj.expenseId || generateSecureId();
      obj.dataHash = hashData(obj);
      obj.syncStatus = obj.syncStatus || SYNC_STATUS.PENDING_CREATE;
      obj.localModifiedAt = Date.now();
      obj.createdDate = Date.now();
      obj.updatedDate = Date.now();
    });

    this.expenses.hook('updating', (modifications, primKey, obj, trans) => {
      modifications.dataHash = hashData({ ...obj, ...modifications });
      modifications.localModifiedAt = Date.now();
      modifications.updatedDate = Date.now();
      modifications.syncStatus = modifications.syncStatus || SYNC_STATUS.PENDING_UPDATE;
    });

    // Hook for customers table
    this.customers.hook('creating', (primKey, obj, trans) => {
      obj.customerId = obj.customerId || generateSecureId();
      obj.dataHash = hashData(obj);
      obj.syncStatus = obj.syncStatus || SYNC_STATUS.PENDING_CREATE;
      obj.localModifiedAt = Date.now();
      obj.createdDate = Date.now();
      obj.updatedDate = Date.now();
    });

    this.customers.hook('updating', (modifications, primKey, obj, trans) => {
      modifications.dataHash = hashData({ ...obj, ...modifications });
      modifications.localModifiedAt = Date.now();
      modifications.updatedDate = Date.now();
      modifications.syncStatus = modifications.syncStatus || SYNC_STATUS.PENDING_UPDATE;
    });

    // Hook for products table
    this.products.hook('creating', (primKey, obj, trans) => {
      obj.productId = obj.productId || generateSecureId();
      obj.dataHash = hashData(obj);
      obj.syncStatus = obj.syncStatus || SYNC_STATUS.PENDING_CREATE;
      obj.localModifiedAt = Date.now();
      obj.createdDate = Date.now();
      obj.updatedDate = Date.now();
    });

    this.products.hook('updating', (modifications, primKey, obj, trans) => {
      modifications.dataHash = hashData({ ...obj, ...modifications });
      modifications.localModifiedAt = Date.now();
      modifications.updatedDate = Date.now();
      modifications.syncStatus = modifications.syncStatus || SYNC_STATUS.PENDING_UPDATE;
    });

    // Hook for money accounts table
    this.moneyAccounts.hook('creating', (primKey, obj, trans) => {
      obj.accountId = obj.accountId || generateSecureId();
      obj.dataHash = hashData(obj);
      obj.syncStatus = obj.syncStatus || SYNC_STATUS.PENDING_CREATE;
      obj.localModifiedAt = Date.now();
      obj.createdDate = Date.now();
      obj.updatedDate = Date.now();
    });

    this.moneyAccounts.hook('updating', (modifications, primKey, obj, trans) => {
      modifications.dataHash = hashData({ ...obj, ...modifications });
      modifications.localModifiedAt = Date.now();
      modifications.updatedDate = Date.now();
      modifications.syncStatus = modifications.syncStatus || SYNC_STATUS.PENDING_UPDATE;
    });

    // Hook for categories table
    this.categories.hook('creating', (primKey, obj, trans) => {
      obj.categoryId = obj.categoryId || generateSecureId();
      obj.dataHash = hashData(obj);
      obj.syncStatus = obj.syncStatus || SYNC_STATUS.PENDING_CREATE;
      obj.localModifiedAt = Date.now();
      obj.createdDate = Date.now();
      obj.updatedDate = Date.now();
    });

    this.categories.hook('updating', (modifications, primKey, obj, trans) => {
      modifications.dataHash = hashData({ ...obj, ...modifications });
      modifications.localModifiedAt = Date.now();
      modifications.updatedDate = Date.now();
      modifications.syncStatus = modifications.syncStatus || SYNC_STATUS.PENDING_UPDATE;
    });

    // Hook for org accounts table
    this.orgAccounts.hook('creating', (primKey, obj, trans) => {
      obj.accountId = obj.accountId || generateSecureId();
      obj.dataHash = hashData(obj);
      obj.syncStatus = obj.syncStatus || SYNC_STATUS.PENDING_CREATE;
      obj.localModifiedAt = Date.now();
      obj.createdDate = Date.now();
      obj.updatedDate = Date.now();
    });

    this.orgAccounts.hook('updating', (modifications, primKey, obj, trans) => {
      modifications.dataHash = hashData({ ...obj, ...modifications });
      modifications.localModifiedAt = Date.now();
      modifications.updatedDate = Date.now();
      modifications.syncStatus = modifications.syncStatus || SYNC_STATUS.PENDING_UPDATE;
    });

    // Hook for sync queue table
    this.syncQueue.hook('creating', (primKey, obj, trans) => {
      obj.queueId = obj.queueId || generateSecureId();
      obj.createdAt = Date.now();
      obj.retryCount = obj.retryCount || 0;
      obj.signature = signData({
        action: obj.action,
        entity: obj.entity,
        entityId: obj.entityId,
        payload: obj.payload,
        createdAt: obj.createdAt
      });
    });
  }

  // Utility methods for data integrity checking
  async checkDataIntegrity() {
    const tamperedRecords = [];
    
    // Check all tables for tampered data
    const tables = ['orders', 'expenses', 'customers', 'products', 'moneyAccounts', 'categories', 'orgAccounts'];
    
    for (const tableName of tables) {
      const table = this[tableName];
      const records = await table.toArray();
      
      for (const record of records) {
        const isValid = verifyDataHash(record, record.dataHash);
        if (!isValid) {
          tamperedRecords.push({
            table: tableName,
            id: record[table.primKey],
            record: record
          });
        }
      }
    }
    
    return tamperedRecords;
  }

  // Mark records as tampered
  async markAsTampered(tableName, recordId) {
    const table = this[tableName];
    await table.update(recordId, {
      syncStatus: SYNC_STATUS.TAMPERED,
      localModifiedAt: Date.now(),
      updatedDate: Date.now()
    });
  }

  // Get pending sync items
  async getPendingSyncItems() {
    const pendingStatuses = [SYNC_STATUS.PENDING_CREATE, SYNC_STATUS.PENDING_UPDATE, SYNC_STATUS.PENDING_DELETE];
    const tables = ['orders', 'expenses', 'customers', 'products', 'moneyAccounts', 'categories', 'orgAccounts'];
    const pendingItems = [];

    for (const tableName of tables) {
      const table = this[tableName];
      const records = await table.where('syncStatus').anyOf(pendingStatuses).toArray();
      pendingItems.push(...records.map(record => ({
        table: tableName,
        record: record
      })));
    }

    return pendingItems;
  }

  // Get sync queue items
  async getSyncQueueItems() {
    return await this.syncQueue.orderBy('createdAt').toArray();
  }

  // Add item to sync queue
  async addToSyncQueue(action, entity, entityId, payload) {
    const queueItem = {
      action,
      entity,
      entityId,
      payload,
      retryCount: 0,
      createdAt: Date.now()
    };
    
    return await this.syncQueue.add(queueItem);
  }

  // Remove item from sync queue
  async removeFromSyncQueue(queueId) {
    return await this.syncQueue.delete(queueId);
  }

  // Update sync status
  async updateSyncStatus(tableName, recordId, status, serverData = null) {
    const table = this[tableName];
    const updates = {
      syncStatus: status,
      localModifiedAt: Date.now(),
      updatedDate: Date.now()
    };

    if (serverData) {
      updates.serverModifiedAt = serverData.updatedDate || Date.now();
      updates.dataHash = serverData.dataHash || hashData(serverData);
    }

    return await table.update(recordId, updates);
  }

  // Get metadata
  async getMetadata(key) {
    const record = await this.metadata.get(key);
    return record ? JSON.parse(record.value) : null;
  }

  // Set metadata
  async setMetadata(key, value) {
    return await this.metadata.put({
      key,
      value: JSON.stringify(value),
      updatedAt: Date.now()
    });
  }

  // Clear all data (for testing or reset)
  async clearAllData() {
    await this.transaction('rw', this.tables, async () => {
      for (const table of this.tables) {
        await table.clear();
      }
    });
  }

  // Get database statistics
  async getStats() {
    const stats = {};
    const tables = ['orders', 'expenses', 'customers', 'products', 'moneyAccounts', 'categories', 'orgAccounts', 'syncQueue'];
    
    for (const tableName of tables) {
      const table = this[tableName];
      const count = await table.count();
      stats[tableName] = count;
    }
    
    return stats;
  }

  // Clear all sync queue items
  async clearSyncQueue() {
    await this.syncQueue.clear();
    console.log('Sync queue cleared');
  }

  // Clear all pending sync items (mark them as synced)
  async clearPendingSyncItems() {
    const pendingStatuses = [SYNC_STATUS.PENDING_CREATE, SYNC_STATUS.PENDING_UPDATE, SYNC_STATUS.PENDING_DELETE];
    const tables = ['orders', 'expenses', 'customers', 'products', 'moneyAccounts', 'categories', 'orgAccounts'];
    
    for (const tableName of tables) {
      const table = this[tableName];
      await table.where('syncStatus').anyOf(pendingStatuses).modify({
        syncStatus: SYNC_STATUS.SYNCED,
        localModifiedAt: Date.now()
      });
    }
    
    console.log('Pending sync items cleared');
  }

  // Clear all sync data (queue + pending items)
  async clearAllSyncData() {
    await this.clearSyncQueue();
    await this.clearPendingSyncItems();
    console.log('All sync data cleared');
  }
}

// Create and export database instance
export const db = new TrackProfitDB();

// Export schema and constants for use in other modules
export { SYNC_STATUS, SYNC_ACTIONS, ENTITY_TYPES } from './schema';

// Export utility functions
export { hashData, verifyDataHash, signData, verifySignature } from '../utils/dataIntegrity';

